import { UpdateIcon } from '@radix-ui/react-icons';
import { useState } from 'react';
import { Button } from '../ui/button';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription, DialogHeader,
    DialogTitle,
    DialogTrigger
} from "../ui/dialog";
import { DropdownMenuItem } from "../ui/dropdown-menu";

import { Input } from '../ui/input';
import { toast } from '../ui/use-toast';

function AddNewHeightLengthLevelDialog({ competitionClass, dropdown, onUpdateClass }) {
    const [isLoading, setIsLoading] = useState(false);
    const [err, setError] = useState(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [nextLevel, setNextLevel] = useState('');

    // Helper function to determine the label based on competitionClass.name
    const getLabel = () => {
        if (competitionClass.name.includes('Höjd')) return 'Höjd';
        if (competitionClass.name.includes('Längd')) return 'Längd';
        return 'Nivå';
    };

    const addNewLevel = () => {
        const parsedNewLevel = Number(nextLevel);

        if (isNaN(parsedNewLevel) || parsedNewLevel <= 0) {
            toast({
                variant: 'destructive',
                title: `Ogiltig ${getLabel().toLowerCase()}.`,
                description: `Ange en giltig ${getLabel().toLowerCase()}.`,
            });
            return;
        }

        const heightLengthLevels = competitionClass.heightLengthLevels || [];
        const maxLevel = Math.max(...heightLengthLevels.map(Number));

        if (parsedNewLevel <= maxLevel) {
            toast({
                variant: 'destructive',
                title: `${getLabel().toLowerCase()} måste vara större än tidigare.`,
                description: `Den nya ${getLabel().toLowerCase()} (${parsedNewLevel} cm) måste vara större än den nuvarande största ${getLabel().toLowerCase()} (${maxLevel} cm).`,
            });
            return;
        }

        // Filter registrations that pass the criteria for the new level
        const passedRegistrations = competitionClass.registrations.filter(registration => {
            // 1. Exclude bunny if it has a crossedOut date
            if (registration.crossedOut) return false;

            // 2. Then check if it passed the last level
            const maxLevelStr = maxLevel.toFixed(2);
            const lastResult = registration.heightLengthResults?.find(
                (result) => result.height_length === maxLevelStr
            );
            if (!lastResult) return false;

            const passed = [lastResult.attempt1, lastResult.attempt2, lastResult.attempt3].includes(true);
            return passed;
        });

        // Notify the parent with the updated competition class
        const updatedCompetitionClass = {
            ...competitionClass,
            heightLengthLevels: [...heightLengthLevels, parsedNewLevel.toFixed(2)],
            registrations: competitionClass.registrations
                // 1. Exclude all crossed-out
                .filter((registration) => !registration.crossedOut)
                // 2. Then add new level only to passed
                .map((registration) => {
                    if (passedRegistrations.includes(registration)) {
                        return {
                            ...registration,
                            heightLengthResults: [
                                ...(registration.heightLengthResults || []),
                                {
                                    height_length: parsedNewLevel.toFixed(2),
                                    attempt1: null,
                                    attempt2: null,
                                    attempt3: null,
                                },
                            ],
                        };
                    }
                    return registration;
                }),
        };


        onUpdateClass(updatedCompetitionClass);
        toast({
            variant: 'success',
            title: 'Tävling uppdaterad!',
            description: `Ny ${getLabel().toLowerCase()} (${parsedNewLevel} cm) har lagts till.`,
        });

        setIsDialogOpen(false);
    };

    return (
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            {dropdown ? (
                <DropdownMenuItem onSelect={(event) => event.preventDefault()}>
                    <DialogTrigger className='flex items-center w-full'>
                        Lägg till {getLabel()}
                    </DialogTrigger>
                </DropdownMenuItem>
            ) : (
                // Look like a tabtrigger
                <DialogTrigger className='inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm'>
                    Lägg till {getLabel().toLowerCase()}
                </DialogTrigger>
            )}
            <DialogContent className="flex flex-col gap-6">
                <DialogHeader className='text-left'>
                    <DialogTitle>
                        Lägg till {getLabel().toLowerCase()}
                    </DialogTitle>
                    <DialogDescription>
                        Ange höjd/längd för att lägga till en {getLabel().toLowerCase()}. Ny {getLabel().toLowerCase()} måste vara högre än tidigare {getLabel().toLowerCase()}.
                    </DialogDescription>
                </DialogHeader>
                <Input
                    min="0"
                    type="number"
                    onChange={(e) => setNextLevel(e.target.value)} // Store the user input in state
                />
                <div className='grid grid-cols-2 gap-3'>
                    <DialogClose asChild>
                        <Button type='button' variant='secondary'>Avbryt</Button>
                    </DialogClose>
                    <Button
                        onClick={() => addNewLevel(nextLevel)}
                        disabled={isLoading}>
                        {(isLoading) ? <UpdateIcon className='loader-spinner' /> : <>Lägg till</>}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default AddNewHeightLengthLevelDialog;
